import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Spotlight — wraps a section and paints a cursor-following orange glow.
 * Adds the `.masar-spotlight` layer and updates --mx / --my CSS vars.
 */
export function Spotlight({ as: Tag = "section", className = "", children, ...rest }) {
  const ref = useRef(null);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <Tag
      ref={ref}
      onMouseMove={handleMove}
      className={`masar-spotlight ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * TiltCard — a 3D tilt-on-hover card driven by pointer position.
 * Respects touch devices by simply not tilting (pointer events won't fire hover).
 */
export function TiltCard({ className = "", children, max = 8, ...rest }) {
  const ref = useRef(null);
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);

  const rx = useSpring(useTransform(py, [0, 1], [max, -max]), {
    stiffness: 180,
    damping: 16,
  });
  const ry = useSpring(useTransform(px, [0, 1], [-max, max]), {
    stiffness: 180,
    damping: 16,
  });

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    px.set(0.5);
    py.set(0.5);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      className={`masar-tilt ${className}`}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
